(() => {
  if (window.Shopify?.designMode) return;
  const restoreOriginal = event => {
    const image = event.target;
    if (!(image instanceof HTMLImageElement) || !image.dataset.fvOriginal) return;
    if (event.type !== 'error' && !/no-image|placeholder/i.test(image.currentSrc)) return;
    const original = image.dataset.fvOriginal;
    image.removeAttribute('data-fv-original');
    image.removeAttribute('srcset');
    image.src = original;
  };
  document.addEventListener('error', restoreOriginal, true);
  document.addEventListener('load', restoreOriginal, true);
  document.querySelectorAll('img[data-fv-original]').forEach(image => {
    if (image.complete && (!image.naturalWidth || /no-image|placeholder/i.test(image.currentSrc))) {
      restoreOriginal({target: image, type: 'error'});
    }
  });
  const connection = navigator.connection;
  const limited = () => connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '');
  const mobileViewport = window.matchMedia('(max-width: 899px)');
  const secondarySelector = '.fauniva-product-card__secondary,.product-card__image--secondary,.fauniva-bundles__image-alternate';
  const queued = new Set();
  const tasks = [];
  let active = 0;
  let paintReady = false;
  const run = () => {
    if (!paintReady || document.hidden || navigator.onLine === false) return;
    while (active < (limited() ? 1 : 2) && tasks.length) {
      const image = tasks.shift();
      if (!image.isConnected || image.closest('details:not([open]),[hidden],template')) {
        queued.delete(image);
        continue;
      }
      active++;
      prepare(image, 'low').finally(() => { active--; queued.delete(image); run(); });
    }
  };
  const enqueue = image => {
    if (!image || queued.has(image) || (!image.hasAttribute('data-fv-src') && image.complete && image.naturalWidth)) return;
    queued.add(image);
    tasks.push(image);
    run();
  };
  const observed = new WeakSet();
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const image = entry.target;
      if (image.closest('details:not([open]),[hidden],template')) continue;
      const box = image.getBoundingClientRect();
      if (!box.width || !box.height) continue;
      if (image.matches(secondarySelector)) {
        const primary = image.parentElement.querySelector('img:not(.fv-deferred-image)');
        if (primary && !primary.complete) {
          primary.addEventListener('load', () => enqueue(image), {once:true});
          continue;
        }
      }
      enqueue(image);
      observer.unobserve(image);
    }
  }, {rootMargin: `${Math.round(Math.min(limited() ? 350 : 900, Math.max(250, window.innerHeight)))}px 0px`, threshold:0}) : null;
  const observe = root => {
    const selector = 'main img[data-fv-src]';
    for (const image of root.querySelectorAll(selector)) {
      if (observed.has(image) || image.closest('details:not([open]),[hidden],template')) continue;
      if (mobileViewport.matches && image.matches(secondarySelector)) continue;
      observed.add(image);
      if (observer) observer.observe(image);
      else enqueue(image);
    }
  };
  const critical = document.querySelector('.fauniva-hero__artwork,product-gallery img[fetchpriority="high"],main img[fetchpriority="high"]');
  const release = () => {
    if (paintReady) return;
    paintReady = true;
    observe(document);
    run();
  };
  const afterPaint = () => requestAnimationFrame(() => requestAnimationFrame(release));
  if (mobileViewport.matches && critical) {
    // A decoded file alone is not proof that the hero has painted. Native lazy
    // sources remain present throughout; this gate controls extra prefetch only.
    if ('PerformanceObserver' in window) {
      try {
        const paints = new PerformanceObserver(list => {
          if (list.getEntries().some(entry => entry.element === critical)) {
            paints.disconnect();
            afterPaint();
          }
        });
        paints.observe({type:'largest-contentful-paint', buffered:true});
        window.addEventListener('pagehide', () => paints.disconnect(), {once:true});
      } catch (_) { /* Use the load fallback below. */ }
    }
    if (critical.complete && critical.naturalWidth) afterPaint();
    else critical.addEventListener('load', afterPaint, {once:true});
    if (document.readyState === 'complete') afterPaint();
    else window.addEventListener('load', afterPaint, {once:true});
    critical.addEventListener('error', afterPaint, {once:true});
  } else if (!critical || critical.complete) afterPaint();
  else {
    critical.addEventListener('load', afterPaint, {once:true});
    critical.addEventListener('error', afterPaint, {once:true});
    window.addEventListener('load', afterPaint, {once:true});
  }
  const intent = event => {
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (!target) return;
    let container = target.closest('.fauniva-product-card__media,.fauniva-spotlight__media,[data-bundle-card]');
    const toggle = target.closest('[data-bundle-image-toggle]');
    if (toggle) container = toggle.closest('[data-bundle-showroom]')?.querySelector(`[data-bundle-card="${toggle.dataset.bundleImageToggle}"]`);
    if (container) {
      const image = container.querySelector(secondarySelector);
      if (image) void prepare(image, 'auto');
    }
  };
  for (const type of ['pointerover','pointerdown','focusin']) document.addEventListener(type, intent, {passive:true});
  document.addEventListener('toggle', event => {
    if (paintReady && event.target instanceof Element && event.target.matches('details[open]')) observe(event.target);
  }, true);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) run(); });
  window.addEventListener('online', run);
  document.addEventListener('shopify:section:load', () => { if (paintReady) observe(document); });
  const galleries = new WeakMap();
  const decodedImages = new WeakSet();
  const pendingDecodes = new WeakMap();
  const pendingImages = new WeakMap();
  const decode = image => {
    if (!image.naturalWidth || decodedImages.has(image)) return Promise.resolve();
    if (pendingDecodes.has(image)) return pendingDecodes.get(image);
    const pending = (image.decode ? image.decode() : Promise.resolve())
      .then(() => decodedImages.add(image)).catch(() => {})
      .finally(() => pendingDecodes.delete(image));
    pendingDecodes.set(image, pending);
    return pending;
  };

  const prepare = (image, priority = 'low') => {
    if (!image) return Promise.resolve();
    const reveal = () => {
      if (image.complete && image.naturalWidth) image.classList.add('fv-image-ready');
    };
    const deferred = image.hasAttribute('data-fv-src');
    if (!deferred && image.complete && image.naturalWidth) return decode(image).then(reveal);
    if (image.fetchPriority !== 'high' && (!pendingImages.has(image) || priority !== 'low')) image.fetchPriority = priority;
    image.decoding = 'async';
    if (pendingImages.has(image)) return pendingImages.get(image);
    image.loading = 'eager';
    const pending = new Promise(resolve => {
      let timer;
      const done = () => {
        clearTimeout(timer);
        image.removeEventListener('load', done);
        image.removeEventListener('error', done);
        resolve();
      };
      image.addEventListener('load', done, {once: true});
      image.addEventListener('error', done, {once: true});
      timer = setTimeout(done, 15000);
      if (deferred) {
        if (image.dataset.fvSrcset) image.srcset = image.dataset.fvSrcset;
        image.src = image.dataset.fvSrc;
        image.removeAttribute('data-fv-src');
        image.removeAttribute('data-fv-srcset');
      }
      if (image.complete) done();
    }).then(() => decode(image)).then(reveal).finally(() => pendingImages.delete(image));
    pendingImages.set(image, pending);
    return pending;
  };

  const initialiseGallery = gallery => {
    if (galleries.has(gallery)) return galleries.get(gallery)();
    let images = [], queue = [], running = false, ready = false;
    const collect = () => [...gallery.querySelectorAll('media-carousel .product-gallery__media[data-media-type="image"]:not([hidden]) > img')];
    const pump = async () => {
      if (running || !ready || document.hidden || navigator.onLine === false) return;
      running = true;
      while (queue.length && !document.hidden && navigator.onLine !== false && gallery.isConnected) {
        const image = queue.shift();
        if (image.isConnected && !image.closest('[hidden]')) await prepare(image);
      }
      running = false;
    };
    const schedule = selected => {
      images = collect();
      const index = Math.max(0, images.indexOf(selected));
      const candidates = limited()
        ? [images[index + 1]]
        : [...images.slice(index + 1, index + 3), images[index - 1]];
      queue = [...new Set(candidates)].filter(image => image && image !== selected && !decodedImages.has(image));
      void pump();
    };
    const refresh = () => {
      const selected = gallery.querySelector('.product-gallery__media.is-selected > img');
      schedule(selected || collect()[0]);
    };
    galleries.set(gallery, refresh);
    gallery.addEventListener('carousel:select', event => {
      if (!event.target.matches('media-carousel')) return;
      const image = event.detail?.slide?.querySelector(':scope > img');
      if (image) void prepare(image, 'auto');
      if (ready) schedule(image);
    });
    const prepareThumbnail = event => {
      if (event.pointerType === 'touch' || limited()) return;
      const control = event.target.closest('.product-gallery__thumbnail');
      if (!control) return;
      const index = [...gallery.querySelectorAll('.product-gallery__thumbnail')].indexOf(control);
      const slide = gallery.querySelector('media-carousel')?.children[index];
      const image = slide && !slide.hidden ? slide.querySelector(':scope > img') : null;
      if (image) void prepare(image, 'auto');
    };
    gallery.addEventListener('pointerover', prepareThumbnail, {passive: true});
    gallery.addEventListener('focusin', prepareThumbnail);
    images = collect();
    const first = images.find(image => image.fetchPriority === 'high') || images[0];
    const start = () => {
      if (ready) return;
      ready = true;
      schedule(first);
    };
    if (!first || first.complete) start();
    else {
      first.addEventListener('load', start, {once: true});
      first.addEventListener('error', start, {once: true});
    }
  };

  document.querySelectorAll('product-gallery').forEach(initialiseGallery);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) document.querySelectorAll('product-gallery').forEach(initialiseGallery);
  });
  for (const type of ['variant:change', 'product:rerender']) {
    document.addEventListener(type, () => requestAnimationFrame(() => {
      document.querySelectorAll('product-gallery').forEach(initialiseGallery);
    }));
  }

  if (!limited() && HTMLScriptElement.supports?.('speculationrules')) {
    const rules = document.createElement('script');
    rules.type = 'speculationrules';
    rules.textContent = JSON.stringify({prefetch: [{
      where: {selector_matches: 'a[href^="/products/"]:not([href*="?"]):not([href*="#"]):not([download]):not([target]), a[href^="/collections/"]:not([href*="?"]):not([href*="#"]):not([download]):not([target])'},
      eagerness: 'moderate'
    }]});
    document.head.append(rules);
  }
})();
